import * as React from 'react'
import type { AgentModelOption, ChatMessage } from './types'
import {
  GradualAssistantReply,
  renderMarkdown,
  renderTextWithRefs,
} from './markdown'
import {
  AgentWorkflowStagesPanel,
  foldUntaggedEvaluateIntoTags,
  parseWorkflowTags,
  visibleAssistantContent,
} from './workflow'
import {
  AgentQuestionChoices,
  isQuestionInteractive,
  parseAgentQuestions,
  stripAgentQuestions,
} from './questions'
import {
  preferredAudioMimeType,
  RecordingSpectrogram,
  transcribeAudio,
} from './voice'
import { formatChatDuration } from './chat_log'

function useElapsedMs(active: boolean): number {
  const [elapsedMs, setElapsedMs] = React.useState(0)
  React.useEffect(() => {
    if (!active) {
      setElapsedMs(0)
      return
    }
    const startedAt = Date.now()
    setElapsedMs(0)
    const id = window.setInterval(() => setElapsedMs(Date.now() - startedAt), 200)
    return () => window.clearInterval(id)
  }, [active])
  return elapsedMs
}

function DurationMark({
  durationMs,
  live,
}: {
  durationMs?: number
  live?: boolean
}) {
  if (typeof durationMs !== "number" && !live) return null
  const ms = durationMs ?? 0
  return (
    <span
      className="model-editor-chat-duration"
      title={live ? "Time spent waiting for a reply" : "Time to reply"}
      style={{
        marginLeft: live ? 0 : 6,
        opacity: live ? 0.55 : 0.5,
        fontWeight: 400,
        letterSpacing: "0.03em",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {formatChatDuration(ms)}
    </span>
  )
}

interface AgentChatPanelProps {
  listRef: React.RefObject<HTMLDivElement>
  messages: ChatMessage[]
  draft: string
  setDraft: (value: string) => void
  isSending: boolean
  /**
   * Send a turn. With no argument the current `draft` is sent (composer
   * submit). Pass `override` to send that text immediately instead — used
   * by the one-click "Explain model" action and voice transcription so they
   * run without the user having to edit/submit the box.
   */
  onSend: (override?: string, options?: { fromVoice?: boolean }) => void
  /** Cancel the in-flight agent turn (in-flight fetch + remaining tools). */
  onStop?: () => void
  /** False when no API key is available for any provider; disables the composer. */
  configured: boolean
  modelOptions: AgentModelOption[]
  selectedOptionId: string
  onSelectedOptionChange: (optionId: string) => void
  optionsLoading?: boolean
  /** Active team id, used to deep-link to settings when not configured. */
  teamId?: number
  /**
   * Called when the user clicks a `#id` reference link in a message — the
   * editor pans to centre that node. Hover-to-highlight is handled
   * internally via the shared node focus store and needs no callback.
   */
  onActivateRef?: (id: number) => void
  /** Public /share view: process wording, and links highlight map layers. */
  publicView?: boolean
  /** Extra public prompts, shown beside Explain this map. */
  quickPrompts?: string[]
  /** Public view only. False hides the default Explain this map button. */
  showExplainMap?: boolean
  /** Rate an assistant reply as helpful (1) or not helpful (-1). */
  onRateMessage?: (messageId: string, rating: 1 | -1) => void
}

type ChatRenderGroup =
  | { kind: "tools"; items: ChatMessage[] }
  | { kind: "single"; item: ChatMessage }

/**
 * Collapse runs of consecutive `tool` action notes into a single group so they
 * render as one stacked block (rows inside a shared box) instead of a column of
 * separate boxes with gaps between each action.
 */
function groupConsecutiveTools(messages: ChatMessage[]): ChatRenderGroup[] {
  const groups: ChatRenderGroup[] = []
  for (const m of messages) {
    if (m.role === "tool") {
      const last = groups[groups.length - 1]
      if (last && last.kind === "tools") last.items.push(m)
      else groups.push({ kind: "tools", items: [m] })
    } else {
      groups.push({ kind: "single", item: m })
    }
  }
  return groups
}

function AssistantMessageRating({
  messageId,
  rating,
  onRateMessage,
}: {
  messageId: string
  rating?: 1 | -1
  onRateMessage?: (messageId: string, rating: 1 | -1) => void
}) {
  if (!onRateMessage) return null
  return (
    <div className="model-editor-chat-bubble-rating" role="group" aria-label="Rate this reply">
      <button
        type="button"
        className={`model-editor-chat-rate-btn${
          rating === 1 ? " model-editor-chat-rate-btn--active-good" : ""
        }`}
        aria-label="Mark as helpful"
        aria-pressed={rating === 1}
        title="Helpful"
        onClick={() => onRateMessage(messageId, 1)}
      >
        <i className="fas fa-check" aria-hidden />
      </button>
      <button
        type="button"
        className={`model-editor-chat-rate-btn${
          rating === -1 ? " model-editor-chat-rate-btn--active-bad" : ""
        }`}
        aria-label="Mark as not helpful"
        aria-pressed={rating === -1}
        title="Not helpful"
        onClick={() => onRateMessage(messageId, -1)}
      >
        <i className="fas fa-minus" aria-hidden />
      </button>
    </div>
  )
}

export function AgentChatPanel({ listRef, messages, draft, setDraft, isSending, onSend, onStop, configured, modelOptions, selectedOptionId, onSelectedOptionChange, optionsLoading, teamId, onActivateRef, onRateMessage, publicView = false, quickPrompts = [], showExplainMap = true }: AgentChatPanelProps) {
  const canSend = !!draft.trim() && !isSending && configured && !!selectedOptionId
  const seenReplyIdsRef = React.useRef<Set<string>>(new Set())
  const replyAnimateRef = React.useRef<Map<string, boolean>>(new Map())
  const seededReplyIdsRef = React.useRef(false)
  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null)
  const mediaStreamRef = React.useRef<MediaStream | null>(null)
  const audioChunksRef = React.useRef<Blob[]>([])
  const [voiceState, setVoiceState] = React.useState<"idle" | "recording" | "transcribing">("idle")
  const [voiceError, setVoiceError] = React.useState<string | null>(null)
  const [liveStream, setLiveStream] = React.useState<MediaStream | null>(null)

  const stopMediaTracks = React.useCallback(() => {
    mediaStreamRef.current?.getTracks().forEach(track => track.stop())
    mediaStreamRef.current = null
    setLiveStream(null)
  }, [])

  const finishVoiceCapture = React.useCallback(async (blob: Blob) => {
    setVoiceState("transcribing")
    setVoiceError(null)
    try {
      if (blob.size === 0) {
        throw new Error("No audio was captured. Please try again.")
      }
      const text = await transcribeAudio(blob)
      if (!text) {
        throw new Error("No speech detected. Please try again.")
      }
      setVoiceState("idle")
      onSend(text, { fromVoice: true })
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setVoiceError(message)
      setVoiceState("idle")
    }
  }, [onSend])

  const stopRecording = React.useCallback(() => {
    const recorder = mediaRecorderRef.current
    if (!recorder || recorder.state === "inactive") {
      stopMediaTracks()
      setVoiceState("idle")
      return
    }
    // Flip to transcribing immediately; `onstop` uploads the blob.
    setVoiceState("transcribing")
    recorder.stop()
  }, [stopMediaTracks])

  const startRecording = React.useCallback(async () => {
    if (isSending || !configured || !selectedOptionId || voiceState !== "idle") return
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setVoiceError("This browser does not support microphone recording.")
      return
    }
    if (typeof MediaRecorder === "undefined") {
      setVoiceError("This browser does not support audio recording.")
      return
    }

    setVoiceError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mediaStreamRef.current = stream
      setLiveStream(stream)
      audioChunksRef.current = []
      const mimeType = preferredAudioMimeType()
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)

      recorder.ondataavailable = event => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data)
      }
      recorder.onerror = () => {
        stopMediaTracks()
        mediaRecorderRef.current = null
        setVoiceError("Recording failed. Please try again.")
        setVoiceState("idle")
      }
      recorder.onstop = () => {
        const chunks = audioChunksRef.current
        audioChunksRef.current = []
        mediaRecorderRef.current = null
        stopMediaTracks()
        const type = recorder.mimeType || mimeType || "audio/webm"
        void finishVoiceCapture(new Blob(chunks, { type }))
      }

      mediaRecorderRef.current = recorder
      recorder.start()
      setVoiceState("recording")
    } catch (err) {
      stopMediaTracks()
      const message = err instanceof Error ? err.message : String(err)
      setVoiceError(
        /Permission|NotAllowed|Denied/i.test(message)
          ? "Microphone permission was denied."
          : `Could not start recording: ${message}`
      )
      setVoiceState("idle")
    }
  }, [configured, finishVoiceCapture, isSending, selectedOptionId, stopMediaTracks, voiceState])

  const toggleVoiceInput = React.useCallback(() => {
    if (voiceState === "recording") stopRecording()
    else if (voiceState === "idle") void startRecording()
  }, [startRecording, stopRecording, voiceState])

  React.useEffect(() => {
    return () => {
      const recorder = mediaRecorderRef.current
      if (recorder && recorder.state !== "inactive") {
        recorder.onstop = null
        recorder.stop()
      }
      mediaRecorderRef.current = null
      stopMediaTracks()
    }
  }, [stopMediaTracks])

  if (!seededReplyIdsRef.current) {
    messages.forEach(message => seenReplyIdsRef.current.add(message.id))
    seededReplyIdsRef.current = true
  }

  const shouldAnimateReply = React.useCallback((message: ChatMessage): boolean => {
    if (message.role !== "assistant") return false
    const cached = replyAnimateRef.current.get(message.id)
    if (cached !== undefined) return cached
    if (seenReplyIdsRef.current.has(message.id)) {
      replyAnimateRef.current.set(message.id, false)
      return false
    }
    seenReplyIdsRef.current.add(message.id)
    replyAnimateRef.current.set(message.id, true)
    return true
  }, [])

  const scrollTranscriptToBottom = React.useCallback(() => {
    const el = listRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [listRef])

  React.useEffect(() => {
    if (voiceState === "idle") return
    scrollTranscriptToBottom()
  }, [voiceState, scrollTranscriptToBottom])

  const sendingElapsedMs = useElapsedMs(isSending)
  const selectedOption = modelOptions.find(option => option.id === selectedOptionId)
  const showExplain = !publicView || showExplainMap
  return (
    <>
      {publicView ? (
        selectedOption ? (
          <div className="model-editor-chat-powered-by">
            Powered by: {selectedOption.modelLabel || selectedOption.label}
          </div>
        ) : null
      ) : (
        <div className="model-editor-chat-powered-by">
          <label htmlFor="aegis-model-select" className="model-editor-chat-powered-by-label">
            Model
          </label>
          <select
            id="aegis-model-select"
            className="model-editor-chat-model-select"
            value={selectedOptionId}
            onChange={event => onSelectedOptionChange(event.target.value)}
            disabled={isSending || optionsLoading || modelOptions.length === 0}
            aria-label="Select agent model and API key"
          >
            {optionsLoading && modelOptions.length === 0 ? (
              <option value="">Loading models…</option>
            ) : modelOptions.length === 0 ? (
              <option value="">No models available</option>
            ) : (
              modelOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))
            )}
          </select>
        </div>
      )}
      {(showExplain || quickPrompts.length > 0) && (
      <div className="model-editor-chat-quick-prompts">
        {showExplain && (
        <button
          type="button"
          className="model-editor-chat-quick-prompt"
          onClick={() => onSend(publicView ? "Explain how this map was produced" : "Explain this model")}
          disabled={isSending || !configured}
        >
          {publicView ? "Explain this map" : "Explain this model"}
        </button>
        )}
        {quickPrompts.map(prompt => (
          <button
            key={prompt}
            type="button"
            className="model-editor-chat-quick-prompt"
            onClick={() => onSend(prompt)}
            disabled={isSending || !configured}
          >
            {prompt}
          </button>
        ))}
      </div>
      )}

      <div
        className="model-editor-chat-messages"
        ref={listRef}
        // Inline clamp so we never have to touch the shared model-editor
        // SCSS again (Sprockets caches that bundle in a way that's easy
        // to wedge — see the `tmp/cache/assets` incident). The viewport
        // budget accounts for app chrome (~80px), the chat header / tabs
        // / quick-prompts (~140px) and the composer (~60px); anything
        // beyond that scrolls inside the list rather than pushing the
        // composer off-screen.
        style={{ maxHeight: "calc(100vh - 360px)", overflowY: "auto" }}
      >
        {groupConsecutiveTools(messages).map(group => {
          if (group.kind === "tools") {
            // Consecutive executed-action notes share a single dashed-green
            // box, stacked as rows, instead of each getting its own bordered
            // bubble. Inline-styled (no SCSS) to dodge the Sprockets cache trap.
            return (
              <div
                key={`tools-${group.items[0].id}`}
                className="model-editor-chat-tool-group"
                role="status"
                style={{
                  alignSelf: "stretch",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  margin: "2px 12px",
                  padding: "8px 10px",
                  borderRadius: 8,
                  background: "#e9f7ef",
                  border: "1px dashed #9ed9b8",
                  color: "#1f7a4d",
                  fontSize: 11.5,
                  lineHeight: 1.4,
                }}
              >
                {(publicView
                  ? [{ ...group.items[group.items.length - 1], text: "Updating models" }]
                  : group.items
                ).map((item, index, items) => (
                  <div
                    key={item.id}
                    style={{ display: "flex", alignItems: "flex-start", gap: 8 }}
                  >
                    <i
                      className="fas fa-bolt"
                      aria-hidden
                      style={{ color: "#2fa46a", fontSize: 11, marginTop: 2, flexShrink: 0 }}
                    />
                    <span style={{ flex: 1 }}>{renderTextWithRefs(item.text, onActivateRef)}</span>
                    {index === items.length - 1 && item.durationMs != null ? (
                      <DurationMark durationMs={item.durationMs} />
                    ) : null}
                  </div>
                ))}
              </div>
            )
          }
          const message = group.item
          if (message.role === "plan") {
            // The batched tool-call plan, shown verbatim before execution.
            // Collapsed by default and monospace so it stays out of the way
            // but is easy to scan when expanded.
            // Inline-styled (no SCSS) to avoid the Sprockets cache trap.
            return (
              <details
                key={message.id}
                style={{
                  alignSelf: "stretch",
                  margin: "2px 12px",
                  padding: "6px 10px",
                  borderRadius: 8,
                  background: "#f1ecfb",
                  border: "1px dashed #c3a8e8",
                  color: "#5a3b86",
                  fontSize: 11.5,
                  lineHeight: 1.4,
                }}
              >
                <summary
                  style={{
                    cursor: "pointer",
                    listStyle: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontWeight: 600,
                  }}
                >
                  <i className="fas fa-list-ol" aria-hidden style={{ color: "#7e57c2", fontSize: 11 }} />
                  <span>Plan</span>
                  <span style={{ marginLeft: "auto", opacity: 0.6, fontWeight: 400 }}>
                    {message.timestamp}
                    <DurationMark durationMs={message.durationMs} />
                  </span>
                </summary>
                <pre
                  style={{
                    marginTop: 6,
                    marginBottom: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                    fontSize: 11,
                    color: "#4a2f70",
                    background: "rgba(255,255,255,0.5)",
                    padding: "6px 8px",
                    borderRadius: 6,
                    border: "1px solid rgba(126, 87, 194, 0.2)",
                  }}
                >
                  {message.text}
                </pre>
              </details>
            )
          }
          if (message.role === "thinking") {
            const thinkingText = foldUntaggedEvaluateIntoTags(message.text)
            const workflowSegments = parseWorkflowTags(thinkingText)
            const hasWorkflowMarkup =
              workflowSegments.length > 0 || /<(reason|plan|evaluate)\b/i.test(thinkingText)
            if (hasWorkflowMarkup) {
              return (
                <AgentWorkflowStagesPanel
                  key={message.id}
                  text={thinkingText}
                  timestamp={message.timestamp}
                  durationMs={message.durationMs}
                  onActivateRef={onActivateRef}
                />
              )
            }

            // Generic reasoning trace when the model did not use workflow tags.
            return (
              <details
                key={message.id}
                style={{
                  alignSelf: "stretch",
                  margin: "2px 12px",
                  padding: "6px 10px",
                  borderRadius: 8,
                  background: "#fff8e1",
                  border: "1px dashed #e6c66a",
                  color: "#6b5a1f",
                  fontSize: 11.5,
                  lineHeight: 1.4,
                }}
              >
                <summary
                  style={{
                    cursor: "pointer",
                    listStyle: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontWeight: 600,
                  }}
                >
                  <i className="fas fa-brain" aria-hidden style={{ color: "#a88726", fontSize: 11 }} />
                  <span>Thinking</span>
                  <span style={{ marginLeft: "auto", opacity: 0.6, fontWeight: 400 }}>
                    {message.timestamp}
                    <DurationMark durationMs={message.durationMs} />
                  </span>
                </summary>
                <div
                  style={{
                    marginTop: 6,
                    marginBottom: 0,
                    wordBreak: "break-word",
                    fontSize: 11,
                    color: "#5a4d1c",
                    background: "rgba(255,255,255,0.5)",
                    padding: "6px 8px",
                    borderRadius: 6,
                    border: "1px solid rgba(166, 134, 38, 0.2)",
                  }}
                >
                  {renderMarkdown(message.text, onActivateRef)}
                </div>
              </details>
            )
          }
          const animateReply = shouldAnimateReply(message)
          const workflowInReply =
            message.role === "assistant"
              ? parseWorkflowTags(message.text).length > 0 ||
                /<(reason|plan|evaluate)\b/i.test(message.text)
              : false
          const replyText =
            message.role === "assistant" ? visibleAssistantContent(message.text) : message.text
          const questions =
            message.role === "assistant" ? parseAgentQuestions(replyText) : []
          const displayText =
            message.role === "assistant" ? stripAgentQuestions(replyText) : message.text
          const questionInteractive =
            message.role === "assistant" &&
            questions.length > 0 &&
            isQuestionInteractive(messages, message.id, isSending)

          if (message.role === "assistant" && !displayText && questions.length === 0 && !workflowInReply) {
            return null
          }

          if (message.role === "assistant" && workflowInReply) {
            return (
              <React.Fragment key={message.id}>
                <AgentWorkflowStagesPanel
                  text={foldUntaggedEvaluateIntoTags(message.text)}
                  timestamp={message.timestamp}
                  durationMs={message.durationMs}
                  onActivateRef={onActivateRef}
                />
                {displayText || questions.length > 0 ? (
                  <div
                    className={`model-editor-chat-message model-editor-chat-message--assistant${
                      animateReply ? " model-editor-chat-message--entering" : ""
                    }`}
                  >
                    <span className="model-editor-chat-message-avatar" aria-hidden>
                      <i className="fas fa-robot" />
                    </span>
                    <div className="model-editor-chat-bubble-stack">
                      {displayText ? (
                        <div className="model-editor-chat-bubble">
                          <div className="model-editor-chat-bubble-text">
                            <GradualAssistantReply
                              text={displayText}
                              animate={animateReply}
                              onActivateRef={onActivateRef}
                              onRevealTick={scrollTranscriptToBottom}
                            />
                          </div>
                          <div className="model-editor-chat-bubble-footer">
                            <span className="model-editor-chat-bubble-time">
                              {message.timestamp}
                              <DurationMark durationMs={message.durationMs} />
                            </span>
                            <AssistantMessageRating
                              messageId={message.id}
                              rating={message.rating}
                              onRateMessage={onRateMessage}
                            />
                          </div>
                        </div>
                      ) : null}
                      <AgentQuestionChoices
                        questions={questions}
                        disabled={!questionInteractive}
                        onChoose={option => onSend(option)}
                      />
                    </div>
                  </div>
                ) : null}
              </React.Fragment>
            )
          }

          return (
            <div
              key={message.id}
              className={`model-editor-chat-message model-editor-chat-message--${message.role}${
                message.role === "assistant" && animateReply
                  ? " model-editor-chat-message--entering"
                  : ""
              }`}
            >
              {message.role === "assistant" && (
                <span className="model-editor-chat-message-avatar" aria-hidden>
                  <i className="fas fa-robot" />
                </span>
              )}
              {message.role === "assistant" ? (
                <div className="model-editor-chat-bubble-stack">
                  {displayText ? (
                    <div className="model-editor-chat-bubble">
                      <div className="model-editor-chat-bubble-text">
                        <GradualAssistantReply
                          text={displayText}
                          animate={animateReply}
                          onActivateRef={onActivateRef}
                          onRevealTick={scrollTranscriptToBottom}
                        />
                      </div>
                      <div className="model-editor-chat-bubble-footer">
                        <span className="model-editor-chat-bubble-time">
                          {message.timestamp}
                          <DurationMark durationMs={message.durationMs} />
                        </span>
                        <AssistantMessageRating
                          messageId={message.id}
                          rating={message.rating}
                          onRateMessage={onRateMessage}
                        />
                      </div>
                    </div>
                  ) : null}
                  <AgentQuestionChoices
                    questions={questions}
                    disabled={!questionInteractive}
                    onChoose={option => onSend(option)}
                  />
                </div>
              ) : (
                <div className="model-editor-chat-bubble">
                  <div className="model-editor-chat-bubble-text">
                    {renderTextWithRefs(message.text, onActivateRef)}
                  </div>
                  <span className="model-editor-chat-bubble-time">
                    {message.timestamp}
                    <DurationMark durationMs={message.durationMs} />
                  </span>
                </div>
              )}
              {message.role === "user" && message.fromVoice && (
                <span
                  className="model-editor-chat-voice-badge"
                  title="Transcribed from audio"
                  aria-label="Transcribed from audio"
                >
                  <i className="fas fa-microphone" aria-hidden />
                </span>
              )}
            </div>
          )
        })}
        {isSending && (
          <div className="model-editor-chat-message model-editor-chat-message--assistant">
            <span className="model-editor-chat-message-avatar" aria-hidden>
              <i className="fas fa-robot" />
            </span>
            <div className="model-editor-chat-bubble">
              <p className="model-editor-chat-bubble-text">
                <i className="fas fa-ellipsis fa-fade" aria-hidden /> Thinking…
                {onStop && (
                  <button
                    type="button"
                    className="model-editor-chat-stop-inline"
                    onClick={onStop}
                  >
                    Stop
                  </button>
                )}
              </p>
              <span className="model-editor-chat-bubble-time">
                <DurationMark durationMs={sendingElapsedMs} live />
              </span>
            </div>
          </div>
        )}
        {voiceState === "recording" && (
          <div className="model-editor-chat-message model-editor-chat-message--user">
            <div className="model-editor-chat-bubble model-editor-chat-bubble--recording">
              {liveStream && <RecordingSpectrogram stream={liveStream} />}
              <span className="model-editor-chat-recording-label">Recording…</span>
            </div>
          </div>
        )}
        {voiceState === "transcribing" && (
          <div className="model-editor-chat-message model-editor-chat-message--user">
            <div className="model-editor-chat-bubble">
              <p className="model-editor-chat-bubble-text">
                <i className="fas fa-spinner fa-spin" aria-hidden style={{ marginRight: 6 }} />
                Transcribing…
              </p>
            </div>
          </div>
        )}
      </div>

      {!configured && !optionsLoading && (
        <div
          style={{
            margin: "0 12px 8px",
            padding: "8px 10px",
            borderRadius: 8,
            background: "#fff8e1",
            border: "1px dashed #e6c66a",
            color: "#6b5a1f",
            fontSize: 12,
            lineHeight: 1.4,
          }}
        >
          <i className="fas fa-key" aria-hidden style={{ marginRight: 6, color: "#a88726" }} />
          The AEGIS agent needs an API key.{" "}
          <a href="/user/settings" style={{ color: "#6b5a1f", textDecoration: "underline" }}>
            Add a personal key
          </a>
          {teamId !== undefined ? (
            <>
              {" "}or{" "}
              <a href={`/teams/${teamId}/edit`} style={{ color: "#6b5a1f", textDecoration: "underline" }}>
                configure one for the team
              </a>
            </>
          ) : (
            <span> or configure one for the team</span>
          )}
          .
        </div>
      )}

      {voiceError && (
        <div
          role="alert"
          style={{
            margin: "0 12px 8px",
            padding: "8px 10px",
            borderRadius: 8,
            background: "#fdecea",
            border: "1px dashed #e2a8a2",
            color: "#8a2f28",
            fontSize: 12,
            lineHeight: 1.4,
          }}
        >
          <i className="fas fa-microphone-slash" aria-hidden style={{ marginRight: 6 }} />
          {voiceError}
        </div>
      )}

      <form
        className="model-editor-chat-composer"
        onSubmit={event => {
          event.preventDefault()
          onSend()
        }}
      >
        <textarea
          className="model-editor-chat-input"
          placeholder={
            optionsLoading
              ? "Loading available models…"
              : !configured || !selectedOption
                ? "Set an API key in account or team settings to chat…"
                : voiceState === "recording"
                  ? "Listening… click the mic to send"
                  : voiceState === "transcribing"
                    ? "Transcribing your audio…"
                    : isSending
                      ? "Waiting for reply…"
                      : "Ask AEGIS something…"
          }
          rows={1}
          value={draft}
          disabled={isSending || !configured || voiceState !== "idle"}
          onChange={event => setDraft(event.target.value)}
          onKeyDown={event => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault()
              onSend()
            }
          }}
        />
        <button
          type="button"
          className={`model-editor-chat-mic${
            voiceState === "recording" ? " model-editor-chat-mic--recording" : ""
          }`}
          aria-label={
            voiceState === "recording"
              ? "Stop recording and send"
              : voiceState === "transcribing"
                ? "Transcribing audio"
                : "Record voice message"
          }
          title={
            voiceState === "recording"
              ? "Stop recording and send"
              : voiceState === "transcribing"
                ? "Transcribing…"
                : "Record voice message"
          }
          disabled={
            voiceState === "transcribing" ||
            isSending ||
            !configured ||
            !selectedOptionId
          }
          onClick={toggleVoiceInput}
        >
          <i
            className={`fas ${
              voiceState === "transcribing"
                ? "fa-spinner fa-spin"
                : voiceState === "recording"
                  ? "fa-stop"
                  : "fa-microphone"
            }`}
          />
        </button>
        {isSending ? (
          <button
            type="button"
            className="model-editor-chat-send model-editor-chat-send--stop"
            aria-label="Stop generating"
            title="Stop"
            onClick={onStop}
            disabled={!onStop}
          >
            <i className="fas fa-stop" />
          </button>
        ) : (
          <button
            type="submit"
            className="model-editor-chat-send"
            aria-label="Send message"
            title="Send message"
            disabled={!canSend || voiceState !== "idle"}
          >
            <i className="fas fa-paper-plane" />
          </button>
        )}
      </form>
    </>
  )
}

